import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, CheckCircle2, Mail, ShieldCheck, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { GrantAccessDialog } from "@/components/GrantAccessDialog";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { formatLastLogin, formatLastLoginAbsolute } from "@/lib/last-login";
import { cn } from "@/lib/utils";
import type {
  PickerDepartment,
  Person,
  PersonAnchor,
  PersonPool,
  PosUser,
  PosUserMembership,
  RoleGrant,
  User,
} from "@tilt/admin-sdk";

/**
 * Focused per-person view. Reached from a Manage / Detail link on
 * Partner / Organization / Location detail pages, the People list,
 * or anywhere else that needs to drill into a single human's
 * memberships without surfing the full /people list.
 *
 * Renders identity + status, the full membership list with revoke
 * (POS pool today; admin pool parked behind task #122a until
 * ROLES_JSON_EXPR surfaces user_membership_tbl.id), and a Grant
 * button that opens the same GrantAccessDialog the bulk list uses.
 *
 * The page accepts ``/people/:pool/:id`` so it works for either
 * pool. The pool segment maps directly to ``apiClient.getPerson``.
 *
 * Single source of truth: ``getPerson`` returns the per-pool detail
 * shape (User | PosUser). Header info, status pill, anchor /
 * primary-partner copy, and the Grant dialog all derive from that
 * one fetch - no separate listPeople round-trip that was broken
 * (listPeople's ``q`` searches name/email, never matches a UUID).
 */
export function PersonDetailPage() {
  usePageTitle("Person");
  const params = useParams<{ pool: string; id: string }>();
  const pool = (params.pool === "pos" ? "pos" : "admin") as PersonPool;
  const personId = params.id ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [granting, setGranting] = useState(false);

  const detail = useQuery<User | PosUser>({
    queryKey: ["person-detail", pool, personId],
    queryFn: () =>
      pool === "admin"
        ? apiClient.getPerson<User>("admin", personId)
        : apiClient.getPerson<PosUser>("pos", personId),
    enabled: !!personId,
  });

  const onMutated = () => {
    void qc.invalidateQueries({ queryKey: ["person-detail", pool, personId] });
    void qc.invalidateQueries({ queryKey: ["people"] });
    void qc.invalidateQueries({ queryKey: ["scoped-people"] });
  };

  const toggle = useMutation({
    mutationFn: async (): Promise<void> => {
      const isDisabled = !!detail.data?.is_disabled;
      if (pool === "admin") {
        if (isDisabled) await apiClient.enableUser(personId);
        else await apiClient.disableUser(personId);
        return;
      }
      if (isDisabled) await apiClient.enablePosUser(personId);
      else await apiClient.disablePosUser(personId);
    },
    onSuccess: onMutated,
  });

  const revoke = useMutation({
    mutationFn: (grantId: string) => apiClient.revokeGrant(pool, grantId),
    onSuccess: onMutated,
  });

  if (detail.isPending) {
    return (
      <p className="rounded-lg border border-dashed bg-muted/20 p-5 text-center text-sm text-muted-foreground">
        Loading person…
      </p>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
        Failed to load person: {detail.error ? describe(detail.error) : "not found"}
      </div>
    );
  }

  const data = detail.data;
  const fullName = [data.first_name, data.last_name].filter(Boolean).join(" ");
  const isAdmin = pool === "admin";
  const isDisabled = !!data.is_disabled;

  // Build the unified ``Person`` shape the GrantAccessDialog expects
  // off the per-pool detail. The dialog only reads pool, id, email,
  // name, and (for POS) anchor - all present on the per-pool shape.
  const grantTarget: Person = personFromDetail(pool, personId, data);

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          Back
        </button>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
              {fullName || data.email}
            </h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="h-4 w-4" />
              <span>{data.email}</span>
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                  isAdmin ? "bg-indigo-100 text-indigo-800" : "bg-emerald-100 text-emerald-800",
                )}
              >
                {isAdmin ? "Admin console" : "POS"}
              </span>
              {isDisabled ? (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                  <Ban className="h-3 w-3" />
                  Disabled
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" />
                  Active
                </span>
              )}
            </div>
            {isAdmin && (data as User).primary_partner && (
              <p className="mt-1 text-xs text-muted-foreground">
                Primary workplace · {(data as User).primary_partner.name}
              </p>
            )}
            {!isAdmin && (data as PosUser).anchor && (
              <p className="mt-1 text-xs text-muted-foreground">
                Anchor ·{" "}
                {(data as PosUser).anchor.kind === "partner"
                  ? (data as PosUser).anchor.partner_name
                  : (data as PosUser).anchor.organization_name}
              </p>
            )}
            <p
              className="mt-1 text-xs text-muted-foreground"
              title={formatLastLoginAbsolute(
                isAdmin ? (data as User).last_login_at : (data as PosUser).last_active_at,
              )}
            >
              Last login ·{" "}
              {formatLastLogin(
                isAdmin ? (data as User).last_login_at : (data as PosUser).last_active_at,
              )}
            </p>
          </div>
          <div className="flex flex-shrink-0 gap-2">
            <button
              type="button"
              onClick={() => setGranting(true)}
              className="flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              <ShieldCheck className="h-4 w-4" />
              Grant access
            </button>
            <button
              type="button"
              onClick={() => toggle.mutate()}
              disabled={toggle.isPending}
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm font-medium",
                isDisabled
                  ? "border-slate-300 text-slate-700 hover:bg-slate-50"
                  : "border-red-300 text-red-700 hover:bg-red-50",
                toggle.isPending && "opacity-50",
              )}
            >
              {isDisabled ? "Re-enable" : "Disable"}
            </button>
          </div>
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Memberships
        </h2>
        {pool === "admin" ? (
          <AdminMemberships data={data as User} />
        ) : (
          <PosMemberships
            data={data as PosUser}
            onRevoke={(grantId) => revoke.mutate(grantId)}
            busy={revoke.isPending}
          />
        )}
      </section>

      {granting && (
        <GrantAccessDialog
          person={grantTarget}
          onClose={() => setGranting(false)}
          onCreated={() => {
            onMutated();
            setGranting(false);
          }}
        />
      )}

      <p className="text-xs text-muted-foreground">
        <Link to="/people" className="underline hover:text-foreground">
          ← All people
        </Link>
      </p>
    </div>
  );
}

function AdminMemberships({ data }: { readonly data: User }) {
  const grants: RoleGrant[] = data.roles ?? [];
  if (grants.length === 0) {
    return (
      <p className="rounded-lg border border-dashed bg-muted/20 p-4 text-center text-xs text-muted-foreground">
        No active memberships. Use Grant access to add the first one.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-slate-200 rounded-lg border bg-card">
      {grants.map((g, idx) => (
        <li
          key={`${g.partner_id}:${g.scope_kind ?? "partner"}:${g.organization_id ?? ""}:${g.location_id ?? ""}:${g.role}:${idx}`}
          className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm"
        >
          <div>
            <div className="font-medium text-slate-800">{g.role.replace(/_/g, " ")}</div>
            <div className="text-xs text-muted-foreground">{adminGrantScope(g)}</div>
          </div>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            read-only
          </span>
        </li>
      ))}
    </ul>
  );
}

function PosMemberships({
  data,
  onRevoke,
  busy,
}: {
  readonly data: PosUser;
  readonly onRevoke: (grantId: string) => void;
  readonly busy: boolean;
}) {
  const memberships: PosUserMembership[] = data.memberships ?? [];
  if (memberships.length === 0) {
    return (
      <p className="rounded-lg border border-dashed bg-muted/20 p-4 text-center text-xs text-muted-foreground">
        No active memberships. Use Grant access to add the first one.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-slate-200 rounded-lg border bg-card">
      {memberships.map((m) => (
        <li key={m.id} className="flex flex-col gap-2 px-4 py-2.5 text-sm">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="font-medium text-slate-800">{m.role_slug.replace(/_/g, " ")}</div>
              <div className="text-xs text-muted-foreground">{posGrantScope(m)}</div>
            </div>
            <button
              type="button"
              onClick={() => onRevoke(m.id)}
              disabled={busy}
              className="flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3" />
              Revoke
            </button>
          </div>
          {/* Default department lives only on location-scope memberships
              - the cashier-side TakePaymentWizard auto-resolves this and
              skips the dept picker step. Pre-configuring it here means
              a freshly-onboarded cashier walks straight into Step 2 of
              the sale flow. */}
          {m.scope_kind === "location" && (
            <DefaultDepartmentRow posUserId={data.id} membership={m} />
          )}
        </li>
      ))}
    </ul>
  );
}

function DefaultDepartmentRow({
  posUserId,
  membership,
}: {
  readonly posUserId: string;
  readonly membership: PosUserMembership;
}) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);
  // Local state lets the user pick before saving; effect re-syncs when
  // the upstream value changes (e.g. another tab saved). The mutation
  // also invalidates the detail query, so onSuccess flushes back through
  // this effect.
  const initial = membership.default_department_id ?? "";
  const [selected, setSelected] = useState<string>(initial);
  useEffect(() => {
    setSelected(membership.default_department_id ?? "");
  }, [membership.default_department_id]);

  const picker = useQuery({
    queryKey: ["membership-picker-departments", posUserId, membership.id],
    queryFn: () => apiClient.listMembershipPickerDepartments(posUserId, membership.id),
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: async (next: string | null): Promise<void> => {
      setPending(next ?? "(none)");
      await apiClient.setMembershipDefaultDepartment(posUserId, membership.id, next);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["person-detail"] });
    },
    onSettled: () => setPending(null),
  });

  const departments: PickerDepartment[] = picker.data?.departments ?? [];
  const dirty = selected !== initial;

  // If the picker fetch failed because the membership row's location
  // is gone (e.g. soft-deleted upstream), the API returns an empty
  // departments list with a non-null location_id. Tell the admin
  // explicitly rather than rendering a useless empty dropdown.
  const noDepartments = !picker.isPending && departments.length === 0;

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-dashed border-slate-200 pt-2 text-xs">
      <label htmlFor={`default-dept-${membership.id}`} className="text-muted-foreground">
        Default department
      </label>
      <select
        id={`default-dept-${membership.id}`}
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={picker.isPending || save.isPending || noDepartments}
        className="rounded border border-slate-300 bg-white px-2 py-1 text-xs disabled:opacity-50"
      >
        <option value="">- None -</option>
        {departments.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
            {d.source === "custom" ? " (custom)" : ""}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!dirty || save.isPending}
        onClick={() => save.mutate(selected === "" ? null : selected)}
        className="rounded border border-slate-300 px-2 py-1 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
      >
        {save.isPending ? "Saving…" : "Save"}
      </button>
      {pending && save.isSuccess && <span className="text-[10px] text-emerald-600">Saved</span>}
      {save.isError && <span className="text-[10px] text-red-600">{describe(save.error)}</span>}
      {noDepartments && (
        <span className="text-[10px] text-amber-600">No departments found at this location.</span>
      )}
    </div>
  );
}

function adminGrantScope(g: RoleGrant): string {
  const kind = g.scope_kind ?? "partner";
  if (kind === "partner") return `Partner: ${g.partner_name ?? g.partner_path ?? "-"}`;
  if (kind === "organization")
    return `Organization: ${g.organization_name ?? g.organization_id ?? "-"}`;
  if (kind === "location") return `Location: ${g.location_name ?? g.location_id ?? "-"}`;
  return "-";
}

/** Resolved scope label for POS memberships. Backend now returns
 *  ``partner_name`` / ``organization_name`` / ``location_name``
 *  alongside the ids; we prefer the name and fall back to "(unnamed)"
 *  rather than dumping a raw UUID, which read terribly in the v1
 *  rendering. */
function posGrantScope(m: PosUserMembership): string {
  if (m.scope_kind === "partner") return `Partner: ${m.partner_name ?? "(unnamed)"}`;
  if (m.scope_kind === "organization") return `Organization: ${m.organization_name ?? "(unnamed)"}`;
  if (m.scope_kind === "location") return `Location: ${m.location_name ?? "(unnamed)"}`;
  return "-";
}

/** Build the unified ``Person`` shape from the per-pool detail
 *  response. GrantAccessDialog reads pool, id, email, names, and (for
 *  POS) anchor - all present on the per-pool shape, just under
 *  slightly different field shapes. */
function personFromDetail(pool: PersonPool, id: string, data: User | PosUser): Person {
  const base = {
    id,
    email: data.email,
    first_name: data.first_name,
    last_name: data.last_name,
    is_disabled: !!data.is_disabled,
    last_active_at: null,
    created_at: data.created_at,
    memberships_count: 0,
    roles_summary: [],
  };
  if (pool === "admin") {
    const u = data as User;
    return {
      ...base,
      pool: "admin",
      primary_partner: {
        id: u.primary_partner.id,
        path: u.primary_partner.path,
        name: u.primary_partner.name,
      },
      anchor: null,
    };
  }
  const p = data as PosUser;
  const anchor: PersonAnchor = {
    kind: p.anchor.kind,
    id: p.anchor.id,
    partner_id: p.anchor.partner_id,
    partner_name: p.anchor.partner_name,
    organization_id: p.anchor.organization_id,
    organization_name: p.anchor.organization_name,
  };
  return {
    ...base,
    pool: "pos",
    primary_partner: null,
    anchor,
  };
}
